import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  Flow,
  FlowId,
  FlowUpdateInput,
  GraFxIntegrationSettingsInput,
  RestartRequestStepName,
  SftpIntegrationSettingsInput,
  Run,
  RunId,
  RunStep,
} from '@grafx-vdp/shared';

import { useAuth } from './auth-context.js';
import { listFlows, getFlow, updateFlow, type UpdateFlowResponse } from './flows.js';
import {
  getGrafxIntegration,
  getSftpIntegration,
  updateGrafxIntegration,
  updateSftpIntegration,
  type GrafxIntegrationResponse,
  type SftpIntegrationResponse,
} from './integrations.js';
import { getApiMeta, type ApiMetaResponse } from './meta.js';
import {
  listRuns,
  getRun,
  cancelRun,
  deleteRun,
  restartRun,
  getRunDataset,
  listRunFiles,
  type GetRunDatasetResponse,
  type ListRunFilesResponse,
  type ListRunsParams,
} from './runs.js';
import { triggerFlow, type TriggerFlowResponse } from './trigger.js';

const STALE_TIME_MS = 30_000;
const RUN_POLL_INTERVAL_MS = 30_000;

export const queryKeys = {
  flows: ['flows'] as const,
  flow: (flowId: FlowId) => ['flow', flowId] as const,
  runs: (params: ListRunsParams) => ['runs', params] as const,
  run: (runId: RunId) => ['run', runId] as const,
  runDataset: (runId: RunId, offset: number, limit: number) =>
    ['run', runId, 'dataset', offset, limit] as const,
  runFiles: (runId: RunId) => ['run', runId, 'files'] as const,
  grafxIntegration: ['integrations', 'grafx'] as const,
  sftpIntegration: ['integrations', 'sftp'] as const,
  meta: ['meta'] as const,
};

export function useFlowsQuery(): UseQueryResult<Flow[]> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: queryKeys.flows,
    queryFn: async () => (await listFlows(jwt)).flows,
    staleTime: STALE_TIME_MS,
    enabled: !!jwt,
  });
}

export function useFlowQuery(flowId: FlowId | null | undefined): UseQueryResult<Flow> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: flowId ? queryKeys.flow(flowId) : ['flow', 'none'],
    queryFn: async () => {
      if (!flowId) throw new Error('flowId required');
      return (await getFlow(jwt, flowId)).flow;
    },
    enabled: !!jwt && !!flowId,
    staleTime: STALE_TIME_MS,
  });
}

export function useRunsForFlowQuery(
  params: ListRunsParams | null,
): UseQueryResult<Run[]> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: params ? queryKeys.runs(params) : ['runs', 'none'],
    queryFn: async () => {
      if (!params) throw new Error('flowId required');
      return (await listRuns(jwt, params)).runs;
    },
    enabled: !!jwt && !!params,
    refetchInterval: (query) => {
      const data = query.state.data as Run[] | undefined;
      if (!data) return false;
      const hasActive = data.some((r) => r.status === 'queued' || r.status === 'running');
      return hasActive ? RUN_POLL_INTERVAL_MS : false;
    },
  });
}

/**
 * Aggregate runs across every flow the principal can see.
 *
 * Phase 0 backend (`GET /api/runs`) requires a `flowId`, so we fan-out one
 * query per flow and merge client-side. Acceptable for Phase 0 (1 tenant, 1
 * flow). Move this to a server-side aggregate in Phase 1 if tenants grow to
 * many flows.
 */
export function useAllRunsQuery(limit = 50): {
  data: Array<Run & { flowName: string }>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  hasActiveRun: boolean;
  refetch: () => Promise<unknown>;
} {
  const { jwt } = useAuth();
  const flowsQuery = useFlowsQuery();
  const flows = flowsQuery.data ?? [];

  const runQueries = useQueries({
    queries: flows.map((flow) => ({
      queryKey: queryKeys.runs({ flowId: flow.id, limit }),
      queryFn: async () => (await listRuns(jwt, { flowId: flow.id, limit })).runs,
      enabled: !!jwt,
      staleTime: STALE_TIME_MS,
      refetchInterval: (query: { state: { data?: Run[] } }) => {
        const data = query.state.data;
        if (!data) return false as const;
        const hasActive = data.some((r) => r.status === 'queued' || r.status === 'running');
        return hasActive ? RUN_POLL_INTERVAL_MS : (false as const);
      },
    })),
  });

  const data: Array<Run & { flowName: string }> = [];
  let isLoading = flowsQuery.isLoading;
  let isFetching = flowsQuery.isFetching;
  let isError = flowsQuery.isError;
  let hasActiveRun = false;

  flows.forEach((flow, i) => {
    const q = runQueries[i];
    if (!q) return;
    if (q.isLoading) isLoading = true;
    if (q.isFetching) isFetching = true;
    if (q.isError) isError = true;
    for (const run of q.data ?? []) {
      if (run.status === 'queued' || run.status === 'running') hasActiveRun = true;
      data.push({ ...run, flowName: flow.name });
    }
  });

  data.sort((a, b) => b.startedAt.localeCompare(a.startedAt));

  const refetch = async () => {
    await flowsQuery.refetch();
    await Promise.all(runQueries.map((q) => q.refetch()));
  };

  return { data, isLoading, isFetching, isError, hasActiveRun, refetch };
}

export function useRunQuery(runId: RunId | null | undefined): UseQueryResult<{
  run: Run;
  steps: RunStep[];
}> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: runId ? queryKeys.run(runId) : ['run', 'none'],
    queryFn: async () => {
      if (!runId) throw new Error('runId required');
      return getRun(jwt, runId);
    },
    enabled: !!jwt && !!runId,
    refetchInterval: (query) => {
      const data = query.state.data as { run: Run } | undefined;
      if (!data) return false;
      const status = data.run.status;
      return status === 'queued' || status === 'running' ? RUN_POLL_INTERVAL_MS : false;
    },
  });
}

export function useCancelRunMutation() {
  const { jwt } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: RunId) => cancelRun(jwt, runId),
    onSuccess: (data, runId) => {
      queryClient.setQueryData(queryKeys.run(runId), data);
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
    },
  });
}

export function useDeleteRunMutation() {
  const { jwt } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: RunId) => deleteRun(jwt, runId),
    onSuccess: (_data, runId) => {
      queryClient.removeQueries({ queryKey: queryKeys.run(runId) });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
    },
  });
}

export function useApiMetaQuery(): UseQueryResult<ApiMetaResponse> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: queryKeys.meta,
    queryFn: () => getApiMeta(jwt),
    staleTime: STALE_TIME_MS,
    enabled: !!jwt,
  });
}

export function useRestartRunMutation() {
  const { jwt } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, fromStep }: { runId: RunId; fromStep: RestartRequestStepName }) =>
      restartRun(jwt, runId, { fromStep }),
    onSuccess: (data, { runId }) => {
      queryClient.setQueryData(queryKeys.run(runId), data);
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
    },
  });
}

export function useRunDatasetQuery(
  runId: RunId | null | undefined,
  offset = 0,
  limit = 50,
): UseQueryResult<GetRunDatasetResponse> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: runId ? queryKeys.runDataset(runId, offset, limit) : ['run', 'none', 'dataset'],
    queryFn: async () => {
      if (!runId) throw new Error('runId required');
      return getRunDataset(jwt, runId, { offset, limit });
    },
    enabled: !!jwt && !!runId,
    staleTime: STALE_TIME_MS,
  });
}

export function useRunFilesQuery(
  runId: RunId | null | undefined,
): UseQueryResult<ListRunFilesResponse> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: runId ? queryKeys.runFiles(runId) : ['run', 'none', 'files'],
    queryFn: async () => {
      if (!runId) throw new Error('runId required');
      return listRunFiles(jwt, runId);
    },
    enabled: !!jwt && !!runId,
    staleTime: STALE_TIME_MS,
  });
}

export function useGrafxIntegrationQuery(): UseQueryResult<GrafxIntegrationResponse> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: queryKeys.grafxIntegration,
    queryFn: () => getGrafxIntegration(jwt),
    staleTime: STALE_TIME_MS,
    enabled: !!jwt,
  });
}

export function useUpdateGrafxIntegrationMutation() {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  return useMutation<GrafxIntegrationResponse, Error, GraFxIntegrationSettingsInput>({
    mutationFn: (body) => updateGrafxIntegration(jwt, body),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.grafxIntegration, data);
      qc.invalidateQueries({ queryKey: queryKeys.grafxIntegration });
    },
  });
}

export function useSftpIntegrationQuery(): UseQueryResult<SftpIntegrationResponse> {
  const { jwt } = useAuth();
  return useQuery({
    queryKey: queryKeys.sftpIntegration,
    queryFn: () => getSftpIntegration(jwt),
    staleTime: STALE_TIME_MS,
    enabled: !!jwt,
  });
}

export function useUpdateSftpIntegrationMutation() {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  return useMutation<SftpIntegrationResponse, Error, SftpIntegrationSettingsInput>({
    mutationFn: (body) => updateSftpIntegration(jwt, body),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.sftpIntegration, data);
      qc.invalidateQueries({ queryKey: queryKeys.sftpIntegration });
    },
  });
}

export function useUpdateFlowMutation() {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  return useMutation<UpdateFlowResponse, Error, { flowId: FlowId; input: FlowUpdateInput }>({
    mutationFn: ({ flowId, input }) => updateFlow(jwt, flowId, input),
    onSuccess: (_data, { flowId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.flows });
      qc.invalidateQueries({ queryKey: queryKeys.flow(flowId) });
    },
  });
}

export function useTriggerFlowMutation() {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  return useMutation<TriggerFlowResponse, Error, { flowId: FlowId; payload?: Record<string, unknown> }>({
    mutationFn: ({ flowId, payload }) => triggerFlow(jwt, flowId, payload),
    onSuccess: (_data, { flowId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.runs({ flowId }) });
      qc.invalidateQueries({ queryKey: ['runs'] });
    },
  });
}
